import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { LogOut, LayoutDashboard, UserRound, ShieldCheck, Landmark, ShoppingBag, Bell, Briefcase, FileText, MessageSquare, LifeBuoy } from "lucide-react";
import { api } from "@/lib/api";

// Shared authenticated frame for /client/* — redirects to /client/login when no session.
export const useClientUser = () => {
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => { sessionStorage.setItem("post_auth_redirect", window.location.pathname); navigate("/client/login"); });
  }, [navigate]);
  return user;
};

export const useClientLogout = () => {
  const navigate = useNavigate();
  return async () => { await api.post("/auth/logout").catch(() => {}); navigate("/"); };
};

const MODULES = [
  { to: "/client/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/client/profile", label: "Profile", icon: UserRound },
  { to: "/client/demat", label: "Demat", icon: Landmark },
  { to: "/client/orders", label: "Orders", icon: ShoppingBag },
  { to: "/client/portfolio", label: "Portfolio", icon: Briefcase },
  { to: "/client/kyc", label: "KYC", icon: ShieldCheck },
  { to: "/client/documents", label: "Documents", icon: FileText },
  { to: "/client/enquiries", label: "Enquiries", icon: MessageSquare },
  { to: "/client/notifications", label: "Alerts", icon: Bell },
  { to: "/client/support", label: "Support", icon: LifeBuoy },
];

export const ClientShell = ({ user, title, eyebrow = "Client Portal", children }) => {
  const logout = useClientLogout();
  return (
    <div data-testid="client-portal" className="min-h-screen bg-white text-slate-900">
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6 lg:px-10">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo.png" alt="PRE-IPO EXPERT" className="h-10 w-10 rounded-full object-contain" />
            <span className="hidden font-display text-lg font-semibold tracking-[0.18em] sm:inline">PRE-IPO <span className="text-[#6D28D9]">EXPERT</span></span>
          </Link>
          <nav className="flex items-center gap-2">
            <Link data-testid="client-nav-dashboard" to="/client/dashboard" className="flex items-center gap-2 border border-slate-200 px-3 py-2 font-mono2 text-[11px] uppercase tracking-[0.2em] text-slate-600 hover:text-[#6D28D9]"><LayoutDashboard className="h-3.5 w-3.5" /><span className="hidden sm:inline">Dashboard</span></Link>
            <Link data-testid="client-nav-profile" to="/client/profile" className="flex items-center gap-2 border border-slate-200 px-3 py-2 font-mono2 text-[11px] uppercase tracking-[0.2em] text-slate-600 hover:text-[#6D28D9]"><UserRound className="h-3.5 w-3.5" /><span className="hidden sm:inline">Profile</span></Link>
            {user?.role === "admin" && <Link data-testid="client-nav-admin" to="/admin" className="flex items-center gap-2 border border-[#6D28D9]/40 px-3 py-2 font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#6D28D9]"><ShieldCheck className="h-3.5 w-3.5" /><span className="hidden sm:inline">Admin</span></Link>}
            <button data-testid="client-logout-button" onClick={logout} className="flex items-center gap-2 border border-slate-200 px-3 py-2 font-mono2 text-[11px] uppercase tracking-[0.2em] text-slate-600 hover:border-rose-300 hover:text-rose-600"><LogOut className="h-3.5 w-3.5" /><span className="hidden sm:inline">Logout</span></button>
          </nav>
        </div>
        <div className="border-t border-slate-100 bg-[#F6F4FD]/60">
          <nav data-testid="client-module-nav" className="mx-auto flex max-w-7xl gap-1 overflow-x-auto px-4 lg:px-8 [scrollbar-width:none]">
            {MODULES.map(({ to, label, icon: Icon }) => (
              <NavLink key={to} to={to} data-testid={`client-subnav-${label.toLowerCase()}`} className={({ isActive }) => `flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-3 font-mono2 text-[10px] uppercase tracking-[0.18em] transition-colors ${isActive ? "border-[#6D28D9] text-[#6D28D9]" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
                <Icon className="h-3.5 w-3.5" /> {label}
              </NavLink>
            ))}
          </nav>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-6 py-10 lg:px-10">
        <p className="font-mono2 text-[11px] uppercase tracking-[0.35em] text-[#6D28D9]">{eyebrow}</p>
        <h1 data-testid="client-page-title" className="font-display mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">{title}</h1>
        {children}
      </main>
    </div>
  );
};
